/**
 * Pure logic for the Daily Rewards page: constants, the GDD defaults, a
 * message-for-message mirror of the backend's validateDailyEngagementConfig,
 * and the numbers the server derives (per-division amounts, streak
 * multiplier, day rewards, re-spin and guaranteed odds, freeze capacity).
 *
 * Source of truth (backend, read-only):
 * - game-config/types/daily-engagement-config.types.ts (types, defaults,
 *   normalize, validate)
 * - daily-engagement/daily-engagement.rewards.ts (amounts)
 * - daily-engagement/daily-engagement.spin.ts (odds)
 * - daily-engagement/daily-engagement.loyalty.ts (freeze capacity)
 * Keep this file in step with them; the test checks it against the GDD numbers.
 */
import type {
  DailyCardRarity,
  DailyCardSpec,
  DailyCurrency,
  DailyDayConfig,
  DailyDayType,
  DailyEngagementConfig,
  DailyLoyaltyTierName,
  DailySpinBand,
  DailySpinBandName,
  DailySpinOdds,
  DailySpinSegment,
  DailyVaultOption,
} from '@/types';

// ---------------------------------------------------------------------------
// Constants (same order as the backend arrays)
// ---------------------------------------------------------------------------

export const DAILY_CURRENCIES: readonly DailyCurrency[] = ['coins', 'evoTokens', 'matchTokens'];

export const DAILY_DAY_TYPES: readonly DailyDayType[] = [
  'normal',
  'card_chance',
  'guaranteed_milestone',
  'enhanced_milestone',
  'grand_milestone',
];

export const DAILY_CARD_RARITIES: readonly DailyCardRarity[] = [
  'rookie',
  'semi_pro',
  'pro',
  'legendary',
];

export const DAILY_SPIN_SEGMENTS: readonly DailySpinSegment[] = [
  'rookie',
  'semi_pro',
  'pro',
  'legendary',
  'no_card',
];

export const DAILY_SPIN_BAND_NAMES: readonly DailySpinBandName[] = ['low', 'mid', 'high'];

const DAILY_LOYALTY_TIERS: readonly DailyLoyaltyTierName[] = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'legend',
];

export const DAILY_CYCLE_LENGTH = 28;
export const DAILY_MAX_MILESTONE_CARDS = 3;
const DIVISION_MIN = 1;
export const DIVISION_MAX = 21;
export const DIVISIONS: readonly number[] = Array.from(
  { length: DIVISION_MAX - DIVISION_MIN + 1 },
  (_, i) => DIVISION_MIN + i
);

export const CURRENCY_LABELS: Record<DailyCurrency, string> = {
  coins: 'Coins',
  evoTokens: 'EvoTokens',
  matchTokens: 'Match Tokens',
};

export const DAY_TYPE_LABELS: Record<DailyDayType, string> = {
  normal: 'Normal',
  card_chance: 'Card chance (spin)',
  guaranteed_milestone: 'Guaranteed milestone',
  enhanced_milestone: 'Enhanced milestone',
  grand_milestone: 'Grand milestone',
};

export const DAY_TYPE_HELP: Record<DailyDayType, string> = {
  normal: 'Grants its one currency.',
  card_chance: "Grants its one currency and creates today's spin.",
  guaranteed_milestone: 'Grants its cards only (1 to 3).',
  enhanced_milestone: 'Grants all three currencies: base plus the enhanced bonus. No cards.',
  grand_milestone: 'Grants its cards (1 to 3) plus the enhanced bundle.',
};

/** Contract names → in-game labels (backend rarity 0 Common … 3 Legendary). */
export const RARITY_LABELS: Record<DailyCardRarity, string> = {
  rookie: 'Rookie',
  semi_pro: 'Semi-Pro',
  pro: 'Pro',
  legendary: 'Legendary',
};

export const SEGMENT_LABELS: Record<DailySpinSegment, string> = {
  ...RARITY_LABELS,
  no_card: 'No Card',
};

export const BAND_LABELS: Record<DailySpinBandName, string> = {
  low: 'Low',
  mid: 'Mid',
  high: 'High',
};

export const TIER_LABELS: Record<DailyLoyaltyTierName, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
  legend: 'Legend',
};

// ---------------------------------------------------------------------------
// GDD defaults: a verbatim copy of DEFAULT_DAILY_ENGAGEMENT_CONFIG
// ---------------------------------------------------------------------------

const day = (
  cycleDay: number,
  dayType: DailyDayType,
  currency: DailyCurrency | null,
  cards: DailyCardSpec[] = []
): DailyDayConfig => ({ cycleDay, dayType, currency, cards });

const cardsOf = (rarity: DailyCardRarity): DailyCardSpec[] => [{ rarity, count: 1 }];

const odds = (
  rookie: number,
  semi_pro: number,
  pro: number,
  legendary: number,
  no_card: number
): DailySpinOdds => ({ rookie, semi_pro, pro, legendary, no_card });

const option = (
  optionId: string,
  r: Partial<DailyVaultOption['reward']>,
  streakFreezes = 0
): DailyVaultOption => ({
  optionId,
  reward: { coins: 0, evoTokens: 0, matchTokens: 0, cards: [], ...r },
  streakFreezes,
});

/** A fresh copy of the GDD defaults (the server has no reset route). */
export function defaultDailyEngagementConfig(): DailyEngagementConfig {
  const C: DailyCurrency = 'coins';
  const E: DailyCurrency = 'evoTokens';
  const T: DailyCurrency = 'matchTokens';
  return {
    schemaVersion: 1,
    baseRewards: {
      coins: { base: 250, perStep: 50, divisor: 1 },
      evoTokens: { base: 1, perStep: 1, divisor: 5 },
      matchTokens: { base: 1, perStep: 1, divisor: 7 },
    },
    multiplierBands: [
      { fromDay: 1, toDay: 6, multiplier: 1.0 },
      { fromDay: 7, toDay: 10, multiplier: 1.1 },
      { fromDay: 11, toDay: 14, multiplier: 1.2 },
      { fromDay: 15, toDay: 18, multiplier: 1.3 },
      { fromDay: 19, toDay: 22, multiplier: 1.4 },
      { fromDay: 23, toDay: 27, multiplier: 1.5 },
      { fromDay: 28, toDay: 28, multiplier: 1.75 },
    ],
    caps: { coins: 2000, evoTokens: 6, matchTokens: 5 },
    days: [
      day(1, 'normal', C),
      day(2, 'normal', E),
      day(3, 'guaranteed_milestone', null, cardsOf('semi_pro')),
      day(4, 'normal', T),
      day(5, 'card_chance', C),
      day(6, 'normal', E),
      day(7, 'guaranteed_milestone', null, cardsOf('semi_pro')),
      day(8, 'normal', C),
      day(9, 'card_chance', T),
      day(10, 'enhanced_milestone', null),
      day(11, 'normal', C),
      day(12, 'normal', E),
      day(13, 'card_chance', C),
      day(14, 'guaranteed_milestone', null, cardsOf('pro')),
      day(15, 'normal', T),
      day(16, 'normal', C),
      day(17, 'enhanced_milestone', null),
      day(18, 'card_chance', E),
      day(19, 'normal', C),
      day(20, 'normal', T),
      day(21, 'guaranteed_milestone', null, cardsOf('pro')),
      day(22, 'card_chance', C),
      day(23, 'normal', E),
      day(24, 'enhanced_milestone', null),
      day(25, 'normal', C),
      day(26, 'card_chance', T),
      day(27, 'normal', E),
      day(28, 'grand_milestone', null, cardsOf('legendary')),
    ],
    enhancedBonus: { coinsPercent: 25, evoTokens: 1, matchTokens: 1 },
    day28CardId: null,
    ...defaultE3E8Config(),
  };
}

/** The W4.4–W4.6 keys the backend fills into an older stored document. */
function defaultE3E8Config(): Pick<
  DailyEngagementConfig,
  'spin' | 'coinBonusPercent' | 'freezes' | 'loyalty'
> {
  return {
    spin: {
      bands: [
        { band: 'low', fromDivision: 1, toDivision: 6, odds: odds(32, 14, 2, 0, 52) },
        { band: 'mid', fromDivision: 7, toDivision: 15, odds: odds(18, 22, 14, 4, 42) },
        { band: 'high', fromDivision: 16, toDivision: 21, odds: odds(8, 20, 28, 10, 34) },
      ],
      respinSplit: { rookie: 40, semi_pro: 30, pro: 20, legendary: 10 },
      guaranteeAfterMisses: 3,
    },
    coinBonusPercent: 30,
    freezes: { baseCapacity: 2, purchaseCostMatchTokens: 5, freeFreezes: 1 },
    loyalty: {
      tiers: [
        {
          tier: 'bronze',
          threshold: 7,
          freezeCapacityBonus: 0,
          options: [
            option('A', { cards: cardsOf('semi_pro') }),
            option('B', { coins: 600 }),
            option('C', { evoTokens: 3 }),
          ],
        },
        {
          tier: 'silver',
          threshold: 30,
          freezeCapacityBonus: 0,
          options: [
            option('A', { cards: cardsOf('pro') }),
            option('B', { coins: 800 }),
            option('C', { matchTokens: 5 }),
          ],
        },
        {
          tier: 'gold',
          threshold: 90,
          freezeCapacityBonus: 1,
          options: [
            option('A', { cards: cardsOf('pro') }),
            option('B', { coins: 1000 }),
            option('C', { evoTokens: 5, matchTokens: 2 }),
          ],
        },
        {
          tier: 'platinum',
          threshold: 180,
          freezeCapacityBonus: 1,
          options: [
            option('A', { cards: cardsOf('legendary') }),
            option('B', {}, 2),
            option('C', { coins: 1200, matchTokens: 1 }),
          ],
        },
        {
          tier: 'legend',
          threshold: 365,
          freezeCapacityBonus: 1,
          options: [
            option('A', { cards: cardsOf('legendary') }),
            option('B', { coins: 2000 }),
            option('C', { matchTokens: 50 }),
          ],
        },
      ],
    },
  };
}

/**
 * Puts back the nulls the backend's global NullRemovalInterceptor strips from
 * every response (the admin config routes don't opt out with @SkipNullRemoval):
 * `day28CardId` (null = random Legendary) and `currency` on milestone days.
 * Without this the served document fails its own validation.
 */
export function restoreNulls(c: DailyEngagementConfig): DailyEngagementConfig {
  return {
    ...c,
    day28CardId: c.day28CardId ?? null,
    days: (c.days ?? []).map((d) => ({ ...d, currency: d.currency ?? null, cards: d.cards ?? [] })),
  };
}

// ---------------------------------------------------------------------------
// Validation mirror: same rules, same order, same messages as the backend
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNonNegInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

const PERCENT_EPSILON = 1e-6;

function isPercent(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100;
}

function sumsTo100(values: number[]): boolean {
  return Math.abs(values.reduce((a, b) => a + b, 0) - 100) <= PERCENT_EPSILON;
}

/**
 * Every problem with a candidate config, or [] when the server would accept
 * it. A copy of the backend's validateDailyEngagementConfig; the server joins
 * these with '; ' in its 400.
 */
export function validateDailyEngagementConfig(input: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(input)) return ['config must be an object'];

  if (input.schemaVersion !== 1) errors.push('schemaVersion must be 1');

  // Base formulas
  if (!isRecord(input.baseRewards)) {
    errors.push('baseRewards must be an object');
  } else {
    for (const c of DAILY_CURRENCIES) {
      const f = input.baseRewards[c];
      if (!isRecord(f)) {
        errors.push(`baseRewards.${c} must be an object`);
        continue;
      }
      if (!isNonNegInt(f.base)) errors.push(`baseRewards.${c}.base must be an integer >= 0`);
      if (!isNonNegInt(f.perStep)) errors.push(`baseRewards.${c}.perStep must be an integer >= 0`);
      if (!(isNonNegInt(f.divisor) && f.divisor >= 1))
        errors.push(`baseRewards.${c}.divisor must be an integer >= 1`);
    }
  }

  // Multiplier bands: contiguous, ascending, covering 1..28
  if (!Array.isArray(input.multiplierBands) || !input.multiplierBands.length) {
    errors.push('multiplierBands must be a non-empty array');
  } else {
    let expectedFrom = 1;
    input.multiplierBands.forEach((b: unknown, i: number) => {
      if (!isRecord(b)) {
        errors.push(`multiplierBands[${i}] must be an object`);
        return;
      }
      if (b.fromDay !== expectedFrom)
        errors.push(`multiplierBands[${i}].fromDay must be ${expectedFrom}`);
      if (!(Number.isInteger(b.toDay) && (b.toDay as number) >= (b.fromDay as number)))
        errors.push(`multiplierBands[${i}].toDay must be >= fromDay`);
      if (
        !(
          typeof b.multiplier === 'number' &&
          Number.isFinite(b.multiplier) &&
          b.multiplier >= 1 &&
          b.multiplier <= 10
        )
      )
        errors.push(`multiplierBands[${i}].multiplier must be within 1..10`);
      expectedFrom = (Number(b.toDay) || 0) + 1;
    });
    if (expectedFrom !== DAILY_CYCLE_LENGTH + 1)
      errors.push(`multiplierBands must end at day ${DAILY_CYCLE_LENGTH}`);
  }

  // Caps
  if (!isRecord(input.caps)) {
    errors.push('caps must be an object');
  } else {
    for (const c of DAILY_CURRENCIES) {
      if (!(isNonNegInt(input.caps[c]) && (input.caps[c] as number) >= 1))
        errors.push(`caps.${c} must be an integer >= 1`);
    }
  }

  // Enhanced bonus
  if (!isRecord(input.enhancedBonus)) {
    errors.push('enhancedBonus must be an object');
  } else {
    for (const k of ['coinsPercent', 'evoTokens', 'matchTokens']) {
      if (!isNonNegInt(input.enhancedBonus[k]))
        errors.push(`enhancedBonus.${k} must be an integer >= 0`);
    }
  }

  // Day 28 card
  if (input.day28CardId !== null && !isNonNegInt(input.day28CardId)) {
    errors.push('day28CardId must be null (random Legendary) or a player index (integer >= 0)');
  }

  // The 28-day table
  if (!Array.isArray(input.days) || input.days.length !== DAILY_CYCLE_LENGTH) {
    errors.push(`days must be an array of exactly ${DAILY_CYCLE_LENGTH}`);
  } else {
    input.days.forEach((d: unknown, i: number) => errors.push(...validateDay(d, i)));
  }

  errors.push(...validateSpin(input.spin));
  if (!(isNonNegInt(input.coinBonusPercent) && input.coinBonusPercent <= 1000))
    errors.push('coinBonusPercent must be an integer within 0..1000');
  errors.push(...validateFreezes(input.freezes));
  errors.push(...validateLoyalty(input.loyalty));

  return errors;
}

function validateSpin(spin: unknown): string[] {
  if (!isRecord(spin)) return ['spin must be an object'];
  const errors: string[] = [];
  if (!Array.isArray(spin.bands) || spin.bands.length === 0) {
    errors.push('spin.bands must be a non-empty array');
  } else {
    let expectedFrom = 1;
    spin.bands.forEach((b: unknown, i: number) => {
      const at = `spin.bands[${i}]`;
      if (!isRecord(b)) {
        errors.push(`${at} must be an object`);
        return;
      }
      if (!DAILY_SPIN_BAND_NAMES.includes(b.band as DailySpinBandName))
        errors.push(`${at}.band must be one of ${DAILY_SPIN_BAND_NAMES.join(', ')}`);
      if (b.fromDivision !== expectedFrom)
        errors.push(`${at}.fromDivision must be ${expectedFrom}`);
      if (
        !(Number.isInteger(b.toDivision) && (b.toDivision as number) >= (b.fromDivision as number))
      )
        errors.push(`${at}.toDivision must be >= fromDivision`);
      expectedFrom = (Number(b.toDivision) || 0) + 1;
      if (!isRecord(b.odds)) {
        errors.push(`${at}.odds must be an object`);
        return;
      }
      const o = b.odds;
      const values = DAILY_SPIN_SEGMENTS.map((seg) => o[seg]);
      if (!values.every(isPercent))
        errors.push(`${at}.odds needs ${DAILY_SPIN_SEGMENTS.join(', ')}, each a percent 0..100`);
      else if (!sumsTo100(values as number[])) errors.push(`${at}.odds must sum to 100`);
      else if ((o.no_card as number) >= 100)
        errors.push(`${at}.odds must give at least one card segment a chance`);
    });
    if (expectedFrom !== 22) errors.push('spin.bands must end at division 21');
  }
  if (!isRecord(spin.respinSplit)) {
    errors.push('spin.respinSplit must be an object');
  } else {
    const split = spin.respinSplit;
    const values = DAILY_CARD_RARITIES.map((r) => split[r]);
    if (!values.every(isPercent) || !sumsTo100(values as number[]))
      errors.push(
        `spin.respinSplit needs ${DAILY_CARD_RARITIES.join(', ')} percents summing to 100`
      );
  }
  if (!(isNonNegInt(spin.guaranteeAfterMisses) && spin.guaranteeAfterMisses >= 1))
    errors.push('spin.guaranteeAfterMisses must be an integer >= 1');
  return errors;
}

function validateFreezes(f: unknown): string[] {
  if (!isRecord(f)) return ['freezes must be an object'];
  const errors: string[] = [];
  if (!isNonNegInt(f.baseCapacity)) errors.push('freezes.baseCapacity must be an integer >= 0');
  if (!(isNonNegInt(f.purchaseCostMatchTokens) && f.purchaseCostMatchTokens >= 1))
    errors.push('freezes.purchaseCostMatchTokens must be an integer >= 1');
  if (!isNonNegInt(f.freeFreezes)) errors.push('freezes.freeFreezes must be an integer >= 0');
  return errors;
}

function validateLoyalty(l: unknown): string[] {
  if (!isRecord(l)) return ['loyalty must be an object'];
  if (!Array.isArray(l.tiers) || l.tiers.length !== DAILY_LOYALTY_TIERS.length)
    return [`loyalty.tiers must be an array of exactly ${DAILY_LOYALTY_TIERS.length}`];
  const errors: string[] = [];
  let previous = 0;
  l.tiers.forEach((t: unknown, i: number) => {
    const at = `loyalty.tiers[${i}]`;
    if (!isRecord(t)) {
      errors.push(`${at} must be an object`);
      return;
    }
    if (t.tier !== DAILY_LOYALTY_TIERS[i])
      errors.push(`${at}.tier must be ${DAILY_LOYALTY_TIERS[i]}`);
    if (!(isNonNegInt(t.threshold) && t.threshold > previous))
      errors.push(`${at}.threshold must be an integer above ${previous}`);
    previous = isNonNegInt(t.threshold) ? t.threshold : previous;
    if (!isNonNegInt(t.freezeCapacityBonus))
      errors.push(`${at}.freezeCapacityBonus must be an integer >= 0`);
    if (!Array.isArray(t.options) || t.options.length !== 3) {
      errors.push(`${at}.options must be an array of exactly 3`);
      return;
    }
    const ids = new Set<string>();
    t.options.forEach((o: unknown, j: number) => {
      const ot = `${at}.options[${j}]`;
      if (!isRecord(o)) {
        errors.push(`${ot} must be an object`);
        return;
      }
      if (typeof o.optionId !== 'string' || o.optionId.length === 0)
        errors.push(`${ot}.optionId must be a non-empty string`);
      else if (ids.has(o.optionId)) errors.push(`${ot}.optionId must be unique within the tier`);
      else ids.add(o.optionId);
      if (!isNonNegInt(o.streakFreezes)) errors.push(`${ot}.streakFreezes must be an integer >= 0`);
      if (!isRecord(o.reward)) {
        errors.push(`${ot}.reward must be an object`);
        return;
      }
      const r = o.reward;
      for (const c of DAILY_CURRENCIES)
        if (!isNonNegInt(r[c])) errors.push(`${ot}.reward.${c} must be an integer >= 0`);
      if (!Array.isArray(r.cards)) {
        errors.push(`${ot}.reward.cards must be an array`);
        return;
      }
      let cards = 0;
      r.cards.forEach((c: unknown, k: number) => {
        if (
          !isRecord(c) ||
          !DAILY_CARD_RARITIES.includes(c.rarity as DailyCardRarity) ||
          !(isNonNegInt(c.count) && c.count >= 1)
        )
          errors.push(`${ot}.reward.cards[${k}] needs a rarity and a count >= 1`);
        else cards += c.count;
      });
      if (cards > DAILY_MAX_MILESTONE_CARDS)
        errors.push(`${ot}.reward carries more than ${DAILY_MAX_MILESTONE_CARDS} cards`);
      const grantsSomething =
        cards > 0 ||
        DAILY_CURRENCIES.some((c) => (r[c] as number) > 0) ||
        (o.streakFreezes as number) > 0;
      if (!grantsSomething) errors.push(`${ot} grants nothing`);
    });
  });
  return errors;
}

function validateDay(d: unknown, i: number): string[] {
  const at = `days[${i}]`;
  if (!isRecord(d)) return [`${at} must be an object`];
  const errors: string[] = [];
  if (d.cycleDay !== i + 1) errors.push(`${at}.cycleDay must be ${i + 1}`);
  if (!DAILY_DAY_TYPES.includes(d.dayType as DailyDayType)) {
    errors.push(`${at}.dayType must be one of ${DAILY_DAY_TYPES.join(', ')}`);
    return errors;
  }
  if (!Array.isArray(d.cards)) {
    errors.push(`${at}.cards must be an array`);
    return errors;
  }
  let cardTotal = 0;
  d.cards.forEach((c: unknown, j: number) => {
    if (!isRecord(c) || !DAILY_CARD_RARITIES.includes(c.rarity as DailyCardRarity)) {
      errors.push(`${at}.cards[${j}].rarity must be one of ${DAILY_CARD_RARITIES.join(', ')}`);
      return;
    }
    if (!(isNonNegInt(c.count) && c.count >= 1))
      errors.push(`${at}.cards[${j}].count must be an integer >= 1`);
    else cardTotal += c.count;
  });

  const type = d.dayType as DailyDayType;
  const singleCurrency = type === 'normal' || type === 'card_chance';
  if (singleCurrency) {
    if (!DAILY_CURRENCIES.includes(d.currency as DailyCurrency))
      errors.push(`${at}.currency must be one of ${DAILY_CURRENCIES.join(', ')} on a ${type} day`);
  } else if (d.currency !== null) {
    errors.push(`${at}.currency must be null on a ${type} day`);
  }

  const carriesCards = type === 'guaranteed_milestone' || type === 'grand_milestone';
  if (carriesCards) {
    if (cardTotal < 1 || cardTotal > DAILY_MAX_MILESTONE_CARDS)
      errors.push(`${at} (${type}) must carry 1..${DAILY_MAX_MILESTONE_CARDS} cards in total`);
  } else if (d.cards.length > 0) {
    errors.push(`${at} (${type}) must carry no cards`);
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Error paths: every backend message starts with the path it is about
// ---------------------------------------------------------------------------

/** The document path a validation message is about ("days[2]", "spin.bands[0].odds"). */
export function errorPath(message: string): string {
  const head = message.split(' ')[0];
  return head === 'config' ? '' : head;
}

/** Whether `path` is `prefix` or lies below it (`a.b`, `a[0]`), on a path boundary. */
function isUnder(path: string, prefix: string): boolean {
  if (prefix === '') return true;
  if (path === prefix) return true;
  if (!path.startsWith(prefix)) return false;
  const next = path.charAt(prefix.length);
  return next === '.' || next === '[';
}

/** The messages about `prefix` itself or anything below it. */
export function errorsUnder(errors: string[], prefix: string): string[] {
  return errors.filter((e) => isUnder(errorPath(e), prefix));
}

/** The messages about exactly `path`. */
export function errorsAt(errors: string[], path: string): string[] {
  return errors.filter((e) => errorPath(e) === path);
}

/** The backend reports every validation problem in one 400, joined by '; '. */
export function splitServerErrors(message: string): string[] {
  return message
    .split('; ')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Document paths each tab of the page edits, for its problem list and badge. */
export const TAB_PREFIXES = {
  daily: [
    'schemaVersion',
    'baseRewards',
    'multiplierBands',
    'caps',
    'days',
    'enhancedBonus',
    'day28CardId',
    'coinBonusPercent',
  ],
  spin: ['spin'],
  freezes: ['freezes'],
  loyalty: ['loyalty'],
} as const;

// ---------------------------------------------------------------------------
// Rewards (daily-engagement.rewards.ts)
// ---------------------------------------------------------------------------

const FLOOR_EPSILON = 1e-9;

function floorSafe(value: number): number {
  return Math.floor(value + FLOOR_EPSILON);
}

/** amount = base + perStep × floor(division / divisor). */
export function baseAmount(
  config: DailyEngagementConfig,
  currency: DailyCurrency,
  divisionTier: number
): number {
  const f = config.baseRewards[currency];
  return f.base + f.perStep * Math.floor(divisionTier / f.divisor);
}

/** The streak multiplier for a streak day (= the cycle day); 1 when no band covers it. */
export function streakMultiplier(config: DailyEngagementConfig, streakDay: number): number {
  const band = config.multiplierBands.find((b) => streakDay >= b.fromDay && streakDay <= b.toDay);
  return band?.multiplier ?? 1;
}

/** Pre-multiplier amount of each currency a day grants at a division. */
export function dayBaseAmounts(
  config: DailyEngagementConfig,
  dayConfig: DailyDayConfig,
  divisionTier: number
): Record<DailyCurrency, number> {
  const base: Record<DailyCurrency, number> = { coins: 0, evoTokens: 0, matchTokens: 0 };
  switch (dayConfig.dayType) {
    case 'normal':
    case 'card_chance': {
      const currency = dayConfig.currency;
      if (currency && DAILY_CURRENCIES.includes(currency))
        base[currency] = baseAmount(config, currency, divisionTier);
      break;
    }
    case 'enhanced_milestone':
    case 'grand_milestone': {
      const bonus = config.enhancedBonus;
      base.coins = (baseAmount(config, 'coins', divisionTier) * (100 + bonus.coinsPercent)) / 100;
      base.evoTokens = baseAmount(config, 'evoTokens', divisionTier) + bonus.evoTokens;
      base.matchTokens = baseAmount(config, 'matchTokens', divisionTier) + bonus.matchTokens;
      break;
    }
    case 'guaranteed_milestone':
      break;
  }
  return base;
}

export interface DayRewardPreview {
  multiplier: number;
  granted: Record<DailyCurrency, number>;
  /** The cap cut this currency. */
  capped: Record<DailyCurrency, boolean>;
  cards: DailyCardSpec[];
}

/** What a day grants at a division when claimed on its own day: base × multiplier → floor → cap. */
export function computeDayReward(
  config: DailyEngagementConfig,
  dayConfig: DailyDayConfig,
  divisionTier: number,
  streakDay: number = dayConfig.cycleDay
): DayRewardPreview {
  const multiplier = streakMultiplier(config, streakDay);
  const base = dayBaseAmounts(config, dayConfig, divisionTier);
  const granted: Record<DailyCurrency, number> = { coins: 0, evoTokens: 0, matchTokens: 0 };
  const capped: Record<DailyCurrency, boolean> = {
    coins: false,
    evoTokens: false,
    matchTokens: false,
  };
  for (const c of DAILY_CURRENCIES) {
    if (base[c] > 0) {
      const scaled = floorSafe(base[c] * multiplier);
      granted[c] = Math.min(scaled, config.caps[c]);
      capped[c] = scaled > config.caps[c];
    }
  }
  return {
    multiplier,
    granted,
    capped,
    cards: dayConfig.cards.map((c) => ({ rarity: c.rarity, count: c.count })),
  };
}

/** E5 RV coin bonus on the day's granted (already capped) coins; uncapped itself. */
export function coinBonus(grantedCoins: number, coinBonusPercent: number): number {
  return floorSafe((grantedCoins * coinBonusPercent) / 100);
}

// ---------------------------------------------------------------------------
// Spin odds (daily-engagement.spin.ts)
// ---------------------------------------------------------------------------

/** The band for a division (1..21); out-of-range divisions clamp to the first / last band. */
export function spinBandFor(
  config: DailyEngagementConfig,
  divisionTier: number
): DailySpinBand | undefined {
  const bands = config.spin.bands;
  if (bands.length === 0) return undefined;
  return (
    bands.find((b) => divisionTier >= b.fromDivision && divisionTier <= b.toDivision) ??
    (divisionTier < bands[0].fromDivision ? bands[0] : bands[bands.length - 1])
  );
}

/** RV re-spin: No Card halved, the freed half spread over the cards by respinSplit. */
export function respinOdds(
  respinSplit: Record<DailyCardRarity, number>,
  o: DailySpinOdds
): DailySpinOdds {
  const freed = o.no_card / 2;
  return {
    rookie: o.rookie + (freed * respinSplit.rookie) / 100,
    semi_pro: o.semi_pro + (freed * respinSplit.semi_pro) / 100,
    pro: o.pro + (freed * respinSplit.pro) / 100,
    legendary: o.legendary + (freed * respinSplit.legendary) / 100,
    no_card: o.no_card - freed,
  };
}

/** Guaranteed spin (spin or re-spin): the BAND without No Card, renormalised to 100. */
export function guaranteedOdds(o: DailySpinOdds): DailySpinOdds {
  const cards = DAILY_CARD_RARITIES.reduce((sum, r) => sum + o[r], 0);
  const scale = cards > 0 ? 100 / cards : 0;
  return {
    rookie: o.rookie * scale,
    semi_pro: o.semi_pro * scale,
    pro: o.pro * scale,
    legendary: o.legendary * scale,
    no_card: 0,
  };
}

/** The probability (0..1) the client receives for one segment. */
export function wireProbability(percent: number): number {
  return Math.round((percent / 100) * 1e9) / 1e9;
}

export function oddsSum(
  o: Partial<Record<DailySpinSegment, number>>,
  segments = DAILY_SPIN_SEGMENTS
) {
  return segments.reduce((sum, s) => sum + (Number.isFinite(o[s]) ? (o[s] as number) : 0), 0);
}

export function isHundred(sum: number): boolean {
  return Math.abs(sum - 100) <= PERCENT_EPSILON;
}

// ---------------------------------------------------------------------------
// Freezes and loyalty (daily-engagement.loyalty.ts)
// ---------------------------------------------------------------------------

/** Freeze storage cap at tier index (0 = no tier, 1 = bronze … 5 = legend). */
export function freezeCapacity(config: DailyEngagementConfig, tierIndex: number): number {
  return config.loyalty.tiers
    .slice(0, tierIndex)
    .reduce((cap, t) => cap + t.freezeCapacityBonus, config.freezes.baseCapacity);
}

/** A one-line description of what a vault option grants. */
export function describeVaultOption(o: DailyVaultOption): string {
  const parts: string[] = [];
  for (const card of o.reward.cards)
    parts.push(
      `${card.count} ${RARITY_LABELS[card.rarity] ?? card.rarity} card${card.count === 1 ? '' : 's'}`
    );
  for (const c of DAILY_CURRENCIES)
    if (o.reward[c] > 0) parts.push(`${o.reward[c]} ${CURRENCY_LABELS[c]}`);
  if (o.streakFreezes > 0)
    parts.push(`${o.streakFreezes} Streak Freeze${o.streakFreezes === 1 ? '' : 's'}`);
  return parts.length > 0 ? parts.join(' + ') : 'Nothing';
}

// ---------------------------------------------------------------------------
// Editing helpers
// ---------------------------------------------------------------------------

/** The number of cards in a list (bad counts count as 0). */
export function cardTotal(cards: DailyCardSpec[]): number {
  return cards.reduce(
    (sum, c) => sum + (Number.isInteger(c.count) && c.count > 0 ? c.count : 0),
    0
  );
}

/**
 * A day with a new type, its currency and cards adjusted so it stays valid:
 * single-currency days keep (or get) a currency and lose their cards;
 * milestones lose the currency; card milestones keep (or get) one card.
 */
export function withDayType(d: DailyDayConfig, dayType: DailyDayType): DailyDayConfig {
  switch (dayType) {
    case 'normal':
    case 'card_chance':
      return { ...d, dayType, currency: d.currency ?? 'coins', cards: [] };
    case 'enhanced_milestone':
      return { ...d, dayType, currency: null, cards: [] };
    case 'guaranteed_milestone':
    case 'grand_milestone':
      return {
        ...d,
        dayType,
        currency: null,
        cards:
          d.cards.length > 0
            ? d.cards
            : [{ rarity: dayType === 'grand_milestone' ? 'legendary' : 'semi_pro', count: 1 }],
      };
  }
}

type RangeKeys<T> = { from: keyof T; to: keyof T };

/**
 * Set the end of range `i` and move the start of range `i + 1` right after
 * it, so contiguous ranges stay contiguous.
 */
export function setRangeEnd<T>(items: T[], i: number, end: number, keys: RangeKeys<T>): T[] {
  return items.map((item, j) => {
    if (j === i) return { ...item, [keys.to]: end };
    if (j === i + 1) return { ...item, [keys.from]: end + 1 };
    return item;
  });
}

/** Remove range `i`; its neighbour takes over its span. */
export function removeRange<T>(items: T[], i: number, keys: RangeKeys<T>): T[] {
  if (items.length <= 1) return items;
  const removed = items[i];
  const rest = items.filter((_, j) => j !== i);
  if (i > 0) {
    rest[i - 1] = { ...rest[i - 1], [keys.to]: removed[keys.to] };
  } else {
    rest[0] = { ...rest[0], [keys.from]: removed[keys.from] };
  }
  return rest;
}

/**
 * Split range `i` in two (a copy of it covers the upper half). Returns the
 * items unchanged when it covers a single unit.
 */
export function splitRange<T>(items: T[], i: number, keys: RangeKeys<T>): T[] {
  const item = items[i];
  const from = Number(item[keys.from]);
  const to = Number(item[keys.to]);
  if (!Number.isInteger(from) || !Number.isInteger(to) || to <= from) return items;
  const mid = Math.floor((from + to) / 2);
  const lower = { ...item, [keys.to]: mid };
  const upper = { ...structuredClone(item), [keys.from]: mid + 1 };
  return [...items.slice(0, i), lower, upper, ...items.slice(i + 1)];
}

// ---------------------------------------------------------------------------
// Diff summary (unsaved changes)
// ---------------------------------------------------------------------------

export interface ConfigChange {
  path: string;
  before: unknown;
  after: unknown;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Every leaf that differs between two JSON documents (arrays by index). */
export function diffConfigs(before: unknown, after: unknown, path = ''): ConfigChange[] {
  if (Array.isArray(before) && Array.isArray(after)) {
    const out: ConfigChange[] = [];
    const n = Math.max(before.length, after.length);
    for (let i = 0; i < n; i++) {
      const p = `${path}[${i}]`;
      if (i >= before.length) out.push({ path: p, before: undefined, after: after[i] });
      else if (i >= after.length) out.push({ path: p, before: before[i], after: undefined });
      else out.push(...diffConfigs(before[i], after[i], p));
    }
    return out;
  }
  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    return keys.flatMap((k) => diffConfigs(before[k], after[k], path ? `${path}.${k}` : k));
  }
  return Object.is(before, after) ? [] : [{ path, before, after }];
}

/** A compact text form of a changed value. */
export function formatValue(v: unknown): string {
  if (v === undefined) return '(none)';
  if (typeof v === 'number' && !Number.isFinite(v)) return '(empty)';
  return JSON.stringify(v);
}
